import React, { useMemo, useState } from 'react';
import { translate } from '../../Services/translation';
import { DropdownOption } from '../Dropdown';
import StockStatus from '../StockStatus/StockStatus';
import ProductCardProps from './types/ProductCardProps';
import ResponsiveImage from '../ResponsiveImage/ResponsiveImage';
import ProductTag from '../ProductTags/ProductTag';
import ProductCardBuyButton from './ProductCardBuyButton';
import clsx from 'clsx';
import Icon from '../Icon';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import NavisionStockStatus from '../StockStatus/NavisionStockStatus';

const ProductCardLandscape = ({
  product,
  isVariantListing = false,
  lazyLoadImage,
  navisionPrice,
  showBothPrices,
  isInBuyableState,
  navisionStock,
}: ProductCardProps) => {
  const { navisionPrices } = useTypedSelector((state) => state.productListing);

  const [selectedOption, setSelectedOption] = useState<DropdownOption>();

  const articleNumber = useMemo(
    () =>
      product.variantOptions?.length ? selectedOption?.value : product.id,
    [product.id, product.variantOptions?.length, selectedOption?.value]
  );

  const navPrice = navisionPrices?.find(x => x.productId == product.id);

  const defaultPriceSection = (
    <>
      {isInBuyableState && navPrice &&
        <div>
          <h4 className="">
            {navPrice.formattedPrice}
          </h4>
        </div>
      }

      {product.navPriceServiceEnabled ? (
        <p className="text-md text-gray-600">
          {translate('product.recommended-price')} {product.price?.formattedPrice}
        </p>
      )
        :
        (
          <p className="text-md text-gray-600">
            {translate('product.recommended-price-ex')} {product.price?.formattedPrice}
          </p>
        )}

      {product.productFee &&
        product.productFee.length > 0 &&
        product.productFee.map((fee) => (
          <p
            className={clsx('text-sm', 'text-gray-500')}
            key={fee.feeTypeName}
          >
            {translate('product.fee')} ({fee.feeTypeName}){': '}
            {fee.priceModel.formattedPrice}
          </p>
        ))}
    </>
  );

  return (
    <div
      className={clsx(
        'flex',
        'gap-3',
        'bg-white',
        'border',
        'border-solid',
        'border-gray-100',
        'rounded',
        'p-3'
      )}
    >
      <div
        className={clsx(
          'bg-gray-100',
          'w-[72px]',
          'h-[72px]',
          '[&_img]:w-full',
          '[&_img]:aspect-square',
          '[&_img]:object-cover'
        )}
      >
        {product.images && product.images.length > 0 && (
          <a href={product.url}>
            <ResponsiveImage
              {...product.images[0]}
              lazyLoad={lazyLoadImage}
            />
          </a>
        )}
      </div>
      <div className={clsx('flex-1')}>
        {product.tags?.length ? (
          <div className={clsx('flex', 'gap-2', 'mb-2')}>
            {product.tags.map((tag) => (
              <ProductTag
                tagSize="large"
                id={tag.id}
                key={tag.id}
                className="rounded"
              >
                {tag.name}
              </ProductTag>
            ))}
          </div>
        ) : null}
        <div className={clsx('text-md', 'text-gray-500')}>
          {product.brand}
        </div>
        <a
          className={clsx('text-md', 'font-bold', 'lg:text-lg')}
          href={product.url}
        >
          {product.name}
        </a>
        {product.id && (
          <p className={clsx('text-md', 'text-gray-600', 'mb-3')}>
            {translate('product.articleno')} {product.id}
          </p>
        )}
        {isInBuyableState && showBothPrices ? (
          <>
            <h4>
              {navisionPrice?.formattedPrice ??
                translate('product.placeholder.fetching.price')}
            </h4>
            <p className={clsx('text-sm', 'text-gray-500')}>
              {translate('product.recommended-price')}
              {': '}
              {product.price?.formattedPrice}
            </p>
            {product.productFee &&
              product.productFee.length > 0 &&
              product.productFee.map((fee) => (
                <p
                  className={clsx('text-sm', 'text-gray-500')}
                  key={fee.feeTypeName}
                >
                  {translate('product.fee')} ({fee.feeTypeName}){': '}
                  {fee.priceModel.formattedPrice}
                </p>
              ))}
          </>
        ) : (
          defaultPriceSection
        )}
        {product.showNavStock ? (
          <NavisionStockStatus navisionStock={navisionStock} />
        ) : (
          <StockStatus {...product.stockStatus} className="mt-2" />
        )}
      </div>
      {isInBuyableState && (
        <ProductCardBuyButton
          className={clsx(
            'flex-col',
            'max-w-[200px]',
            '[&_input]:py-2',
            '[&_input]:px-3',
            '[&_input]:mw-full',
            '[&_button]:gap-2',
            '[&_button]:justify-center',
            '[&_button]:py-2'
          )}
          product={product}
          isVariantListing={isVariantListing}
          label={
            <>
              <Icon name="shopping-cart" className="h-5 w-5" />
              {translate('general.add')}
            </>
          }
        />
      )}
    </div>
  );
};

export default ProductCardLandscape;
